import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Link as GatsbyLink } from "gatsby";
// import { NoibuLink } from "@sunwing/shared-components-beta";
import { Link } from "@sunwing/wvi-components";
// import { RCL as useTranslation } from "../../components/RCL";
import { cloudinaryGenURL } from "../../utils/cloudinaryHelpers";
import { ImageConnector } from "../ImageConnector";

export const INTERNAL_URL_REGEX = new RegExp(
	"^(https?://(www.)?westjetvacations.com/)|^/(?!/)|^#$"
);

const LinkConnector = forwardRef(
	(
		{
			to,
			target,
			children,
			image,
			imageAltText,
			icon,
			theme,
			size,
			noStyle,
			showExternalIcon,
			activeClassName,
			partiallyActive,
			externalModalMessage,
			...props
		},
		ref
	) => {
		const isInternalLink = INTERNAL_URL_REGEX.test(to);
		const isProtocolLink = to?.startsWith("mailto:") || to?.startsWith("tel:");
		const isNoibuLink = to === "#helpcode";
		const isAnchorLink = to?.includes("#");
		const isAnchorLinkExcludingNoibu = isAnchorLink && !isNoibuLink;
		const isNonHTTPLink = isInternalLink || isProtocolLink || isAnchorLink;
		const isExternalUrl = !isInternalLink && !isProtocolLink;
		let linkChildren = children;

		if (image?.[0]) {
			linkChildren = (
				<ImageConnector
					image={cloudinaryGenURL({ cloudinaryImage: image[0] })}
					alt={imageAltText}
				/>
			);
		}

		/** 
		 * Todo Implement Nobilink
		 * 
		const translations = {
			title: useTranslation({ searchKey: "noibu-link-title" }),
			loadingTitle: useTranslation({ searchKey: "noibu-link-loading" }),
			modalLabels: {
				title: useTranslation({ searchKey: "noibu-link-modal-title" }),
				unavailableMessage: useTranslation({ searchKey: "noibu-link-unavailable" }),
			},
		};

		if (isNoibuLink) {
			return (
				<NoibuLink
					className={props.className}
					title={translations.title}
					loadingTitle={translations.loadingTitle}
					modalLabels={{
						title: translations.modalLabels.title,
						unavailableMessage: translations.modalLabels.unavailableMessage,
					}}
				/>
			);
		}
		*/

		if (isNonHTTPLink && target !== "_blank") {
			const replacedHref = to.replace(INTERNAL_URL_REGEX, "/");
			return (
				<Link
					theme={theme}
					icon={icon}
					noStyle={noStyle}
					href={replacedHref}
					size={size}
					target={target === null ? "_self" : target}
					renderLink={
						isAnchorLinkExcludingNoibu
							? undefined
							: (href, { noStyle: _, ...linkProps }) => (
									<GatsbyLink
										to={href}
										activeClassName={activeClassName}
										partiallyActive={partiallyActive}
										// eslint-disable-next-line react/jsx-props-no-spreading
										{...linkProps}
									/>
								)
					}
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...props}>
					{linkChildren}
				</Link>
			);
		}

		return (
			<Link
				ref={ref}
				href={to}
				icon={icon}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(isExternalUrl &&
					showExternalIcon && {
						rel: "noopener",
						icon: "external-micro",
					})}
				target={!isInternalLink && target === null ? "_blank" : target}
				theme={theme}
				size={size}
				noStyle={noStyle}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}>
				{linkChildren}
			</Link>
		);
	}
);

LinkConnector.displayName = "LinkConnector";

LinkConnector.propTypes = {
	to: PropTypes.string.isRequired,
	target: PropTypes.oneOf(["_self", "_blank", "_parent", "_top"]),
	image: PropTypes.arrayOf(
		PropTypes.shape({
			alt: PropTypes.string,
			title: PropTypes.string,
			width: PropTypes.number,
			height: PropTypes.number,
			data: PropTypes.shape({
				src: PropTypes.string,
				srcSet: PropTypes.string,
				sizes: PropTypes.string,
			}),
		})
	),
	imageAltText: PropTypes.string,
	size: PropTypes.string,
	activeClassName: PropTypes.string,
	partiallyActive: PropTypes.bool,
	className: PropTypes.string,
	children: PropTypes.node.isRequired,
	icon: PropTypes.string,
	theme: PropTypes.oneOf(["primary", "inverse"]),
	noStyle: PropTypes.bool,
	showExternalIcon: PropTypes.bool,
	externalModalMessage: PropTypes.string,
};

LinkConnector.defaultProps = {
	target: "_self",
	image: undefined,
	imageAltText: undefined,
	activeClassName: undefined,
	className: undefined,
	partiallyActive: false,
	icon: undefined,
	theme: "primary",
	noStyle: false,
	size: undefined,
	showExternalIcon: true,
	externalModalMessage: undefined,
};

export { LinkConnector };
